import { Suspense } from 'react';
import { lazyPage } from '@/lib/lazyPage';
import { useTranslation } from 'react-i18next';
import { Capacitor } from '@capacitor/core';
import { getAccessToken } from '@/api/authSession';
import { Toaster } from '@/components/ui/toaster';
import { Toaster as Sonner } from '@/components/ui/sonner';
import { TooltipProvider } from '@/components/ui/tooltip';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { createBrowserRouter, Navigate, RouterProvider, Outlet } from 'react-router-dom';
import PostHogAnalyticsWrapper from '@/components/PostHogAnalyticsWrapper';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import { useRealtimeStream } from '@/hooks/useRealtimeStream';
import { CaptureProvider } from '@/contexts/CaptureContext';
import { AuthenticatedRoute } from '@/components/AuthenticatedRoute';
import { BottomNav } from '@/components/BottomNav';
import { useRefreshFeedOnMustReadsSwitch } from '@/pages/newsfeed/hooks/useRefreshFeedOnMustReadsSwitch';
import { SafeAreaTop } from '@/components/SafeAreaTop';
import { ErrorBoundary } from '@sentry/react';
import { StatusBar } from '@/hooks/useStatusBar';
import { FreshAppShell } from '@/hooks/useFreshAppShell';
import { AuthLinks } from '@/hooks/useAuthLinks';
import { PushNotifications } from './hooks/usePushNotifications';
import { AdminRoute } from './pages/admin/AdminRoute';
const Home = lazyPage(
  () => import('./pages/home'),
  (m) => m.HomePage,
);
const Welcome = lazyPage(
  () => import('./pages/welcome'),
  (m) => m.WelcomePage,
);
const Login = lazyPage(
  () => import('./pages/login'),
  (m) => m.LoginPage,
);
const ForgotPassword = lazyPage(
  () => import('./pages/auth'),
  (m) => m.ForgotPasswordPage,
);
const ResetPassword = lazyPage(
  () => import('./pages/auth'),
  (m) => m.ResetPasswordPage,
);
const AcceptInvite = lazyPage(
  () => import('./pages/auth'),
  (m) => m.AcceptInvitePage,
);
const AdminLayout = lazyPage(
  () => import('./pages/admin'),
  (m) => m.AdminLayout,
);
const AdminUsers = lazyPage(
  () => import('./pages/admin'),
  (m) => m.AdminUsers,
);
const AdminStores = lazyPage(
  () => import('./pages/admin'),
  (m) => m.AdminStores,
);
const AdminIntegrations = lazyPage(
  () => import('./pages/admin'),
  (m) => m.AdminIntegrations,
);
const AdminBranding = lazyPage(
  () => import('./pages/admin'),
  (m) => m.AdminBranding,
);
const AdminHandoversGate = lazyPage(
  () => import('./pages/admin'),
  (m) => m.HandoversFeatureGate,
);
const AdminHandovers = lazyPage(
  () => import('./pages/admin'),
  (m) => m.AdminHandovers,
);
const AdminHandoverTemplateEditor = lazyPage(
  () => import('./pages/admin'),
  (m) => m.AdminHandoverTemplateEditor,
);
const Error = lazyPage(
  () => import('./pages/Error'),
  (m) => m.default,
);
const NotFound = lazyPage(
  () => import('./pages/NotFound'),
  (m) => m.default,
);
const Onboarding = lazyPage(
  () => import('./pages/onboarding'),
  (m) => m.OnboardingPage,
);
const Profile = lazyPage(
  () => import('./pages/Profile'),
  (m) => m.default,
);
const Notes = lazyPage(
  () => import('./pages/notes'),
  (m) => m.NotesPage,
);
const StoreEvents = lazyPage(
  () => import('./pages/store-events'),
  (m) => m.StoreEventsPage,
);
const Academy = lazyPage(
  () => import('./pages/academy'),
  (m) => m.AcademyPage,
);
const Tasks = lazyPage(
  () => import('./pages/tasks/Tasks'),
  (m) => m.TasksPage,
);
const Handover = lazyPage(
  () => import('./pages/handover/Handover'),
  (m) => m.HandoverRoute,
);
const HandoverHistory = lazyPage(
  () => import('./pages/handover/HandoverHistory'),
  (m) => m.HandoverHistoryPage,
);
const ShiftRequestNew = lazyPage(
  () => import('./pages/shift-requests/ShiftRequestNew'),
  (m) => m.ShiftRequestNewPage,
);
const ShiftRequestDetail = lazyPage(
  () => import('./pages/shift-requests/ShiftRequestDetail'),
  (m) => m.ShiftRequestDetailPage,
);
const TaskTemplates = lazyPage(
  () => import('./pages/task-templates/TaskTemplates'),
  (m) => m.TaskTemplatesPage,
);
const Notifications = lazyPage(
  () => import('./pages/app-notifications'),
  (m) => m.AppNotifications,
);
const Newsfeed = lazyPage(
  () => import('./pages/newsfeed'),
  (m) => m.NewsfeedPage,
);
const Calendar = lazyPage(
  () => import('./pages/calendar'),
  (m) => m.CalendarPage,
);
const NewsfeedDetail = lazyPage(
  () => import('./pages/newsfeed'),
  (m) => m.NewsfeedDetailPage,
);

const queryClient = new QueryClient();

const PageLoader = () => {
  const { t } = useTranslation('index');
  return (
    <div
      className='flex min-h-screen-safe items-center justify-center'
      role='status'
      aria-label={t('aria.loading-page')}
    >
      <span className='text-muted-foreground'>{t('states.loading')}</span>
    </div>
  );
};

/** Layout for main app screens: outlet + bottom nav. Shown only when authenticated and onboarding complete. */
const MainLayout = () => {
  const { user } = useAuth();
  // Single realtime connection for the whole authenticated session. Native sessions have no
  // cookie, so they hand the stream their access token.
  useRealtimeStream(Boolean(user), Capacitor.isNativePlatform() ? getAccessToken : undefined);
  useRefreshFeedOnMustReadsSwitch();

  return (
    <>
      <Suspense fallback={<PageLoader />}>
        <Outlet />
      </Suspense>
      <BottomNav />
    </>
  );
};

/** Root layout: providers that require the router context live here. */
const RootLayout = () => (
  <AuthProvider>
    <CaptureProvider>
      <AuthLinks />
      <PushNotifications />
      <StatusBar />
      <SafeAreaTop />
      <FreshAppShell />
      <Toaster />
      <Sonner />
      <Suspense fallback={<PageLoader />}>
        <Outlet />
      </Suspense>
    </CaptureProvider>
  </AuthProvider>
);

const router = createBrowserRouter([
  {
    element: <RootLayout />,
    children: [
      {
        path: '/',
        element: (
          <AuthenticatedRoute>
            <MainLayout />
          </AuthenticatedRoute>
        ),
        children: [
          { index: true, element: <Home /> },
          { path: 'tasks', element: <Tasks /> },
          { path: 'tasks/templates', element: <TaskTemplates /> },
          { path: 'handovers/history', element: <HandoverHistory /> },
          { path: 'handovers/:id', element: <Handover /> },
          { path: 'shift-requests/new', element: <ShiftRequestNew /> },
          { path: 'shift-requests/:requestId', element: <ShiftRequestDetail /> },
          { path: 'profile', element: <Profile /> },
          { path: 'notes', element: <Notes /> },
          { path: 'store-events', element: <StoreEvents /> },
          { path: 'academy/*', element: <Academy /> },
          { path: 'notifications', element: <Notifications /> },
          { path: 'calendar', element: <Calendar /> },
          { path: 'community/newsfeed', element: <Newsfeed /> },
          { path: 'community/newsfeed/:postId', element: <NewsfeedDetail /> },
          {
            path: 'admin',
            element: (
              <AdminRoute>
                <AdminLayout />
              </AdminRoute>
            ),
            children: [
              {
                index: true,
                element: (
                  <Navigate
                    to='users'
                    replace
                  />
                ),
              },
              { path: 'users', element: <AdminUsers /> },
              { path: 'stores', element: <AdminStores /> },
              {
                path: 'handovers',
                element: <AdminHandoversGate />,
                children: [
                  { index: true, element: <AdminHandovers /> },
                  { path: 'new', element: <AdminHandoverTemplateEditor /> },
                  { path: ':templateId', element: <AdminHandoverTemplateEditor /> },
                ],
              },
              { path: 'integrations', element: <AdminIntegrations /> },
              { path: 'branding', element: <AdminBranding /> },
            ],
          },
        ],
      },
      { path: '/welcome', element: <Welcome /> },
      { path: '/login', element: <Login /> },
      { path: '/auth/forgot', element: <ForgotPassword /> },
      { path: '/auth/reset', element: <ResetPassword /> },
      { path: '/auth/invite', element: <AcceptInvite /> },
      {
        path: '/onboarding',
        element: (
          <AuthenticatedRoute isOnboardingPage>
            <Onboarding />
          </AuthenticatedRoute>
        ),
      },
      { path: '/error', element: <Error /> },
      { path: '*', element: <NotFound /> },
    ],
  },
]);

const App = () => (
  <ErrorBoundary fallback={<PageLoader />}>
    <PostHogAnalyticsWrapper>
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <RouterProvider router={router} />
        </TooltipProvider>
      </QueryClientProvider>
    </PostHogAnalyticsWrapper>
  </ErrorBoundary>
);

export default App;
